import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  isArray as _isArray,
  debounce as _debounce,
  find as _find,
  get as _get,
  map as _map,
  reduce as _reduce,
  round as _round,
} from 'lodash';

import {
  addToCart,
  fetchProduct,
  fetchReviews,
  hideMobileProductSelect,
  setBuyBarState,
  setFeature,
  setHeadroomState,
  showMobileProductSelect,
  showModal,
  updateSelectedVariant,
} from 'actions';

import {
  CASPER_CORE,
  CASPER_ESSENTIAL,
  CASPER_WAVE,
} from 'routes/productsConfig';
import { constructPagePath } from 'utils/localeRouteGuide';
import { currencyFormat } from 'utils/currencyFormat';
import { locale } from 'utils/locale';
import { mQ } from 'utils/mediaQuery';
import { scrollToElementWithBuyBar } from 'utils/scroll';
import { optionalAttribute } from 'utils/jsx';

import { addedProductPDP as trackAddedProductPDP } from 'analytics/cart';
import { hoverDebounce } from 'analytics/helpers';
import { selectSize as trackSelectSize } from 'analytics/product';
import { showReviewsPopover as trackShowReviewsPopover} from 'analytics/reviews';
import { showDeliveryMessage as trackShowDeliveryMessage} from 'analytics/messaging';
import { viewedProductPDP as trackViewedProductPDP } from 'analytics/product';
import { viewedProduct as lpViewedProduct } from 'analytics/livePerson';

import Dropdown from 'components/Dropdown/Dropdown';
import localize from 'components/Localize/Localize';
import MobileProductSelector from 'components/MobileProductSelector/MobileProductSelector';
import Modal from 'components/Modal/Modal';
import Popover from 'components/Popover/Popover';
import ProductAvailability from 'components/ProductAvailability/ProductAvailability';
import ProductRatings from 'components/ProductRatings/ProductRatings';
import PurchaserRating from 'components/PurchaserRating/PurchaserRating';
import ShippingModal from 'components/ApplicationModals/ShippingModal/ShippingModal';
import SpinningLoader from 'components/SpinningLoader/SpinningLoader';

import styles from './BuyBar.css';
import translations from './translations';

const cx = classNames.bind(styles);

class BuyBar extends React.Component {
  constructor() {
    super();
    this.state = {
      quantity: 1,
      isAdding: false,
      isDesktop: true,
    };

    this.addToCart = this.addToCart.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleAddToCartMobile = this.handleAddToCartMobile.bind(this);
    this.handleLaunchShippingModal = this.handleLaunchShippingModal.bind(this);
    this.handleQuantityUpdate = this.handleQuantityUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleVariantClick = this.handleVariantClick.bind(this);
    this.handleReviewsJumpClick = this.handleReviewsJumpClick.bind(this);
    this.onResize = _debounce(this.handleResize, 200);

    this.buyMap = {
      [CASPER_ESSENTIAL]: `essential-buy-page`,
      [CASPER_CORE]: `core-buy-page`,
      [CASPER_WAVE]: `premium-buy-page`,
    };
  }

  componentWillMount() {
    this.props.setHeadroomState(false);
    this.props.setBuyBarState(true);
  }

  componentDidMount() {
    trackViewedProductPDP({
      category: this.props.analytics.category,
      quantity: `NA`,
      variantData: _find(this.props.variants, {id: this.props.selectedVariantID}),
    }, this.props.locale);

    lpViewedProduct({
      category: this.props.analytics.category,
      variant: _find(this.props.variants, {id: this.props.selectedVariantID}),
      locale: this.props.locale,
    });

    window.addEventListener(`resize`, this.onResize);
    this.handleResize();

    // only update data after the higher order component DidMount
    // runs `componentDidMount`
    this.context.queue.push(() => {
      this.props.getProductData(this.props.productType, this.props.locale);
      this.props.getReviews();
    });
  }

  componentWillUnmount() {
    this.props.setBuyBarState(false);
    window.removeEventListener(`resize`, this.onResize);
  }

  componentWillReceiveProps(newProps) {
    if (this.state.isAdding && !newProps.isAddingToCart) {
      this.setState({ isAdding: false });
    }
  }

  shouldShowRatings() {
    const {
      casperReviewsTotalCount,
      casperReviewsAvgRating,
      ratings,
      showRatings,
    } = this.props;

    return showRatings && casperReviewsTotalCount && casperReviewsAvgRating && _isArray(ratings);
  }

  isBuyPage() {
    return this.props.buyPage && !this.state.isDesktop;
  }

  getSizeOptions(mobile) {
    const sizes = this.props.variants.map((variant) => {
      return {
        label: variant.attributes.options.size,
        value: variant.id,
        optional: {
          price: currencyFormat(variant.attributes.price, this.props.locale, false),
          readyToShip: variant.attributes[`ready-to-ship`],
        },
      };
    });

    if (mobile) {
      const options = [
        {
          label: this.props.t.selectSize,
          value: ``,
        },
        ...sizes,
      ];

      return options;
    }

    return sizes;
  }

  addToCart(id) {
    if (this.state.isAdding) {
      return;
    }

    this.setState({
      isAdding: true,
    }, () => {
      this.props.addToCart(id, this.state.quantity);
      trackAddedProductPDP({
        variantData: _find(this.props.variants, { id }),
        category: this.props.analytics.category,
      }, this.props.locale);
    });
  }

  getProp(field) {
    const matchedVariant = _find(this.props.variants, {id: this.props.selectedVariantID});

    return matchedVariant.attributes[field];
  }

  getAvailabilityProps() {
    return {
      date: this.getProp(`ships-by`),
      isReadyToShip: this.getProp(`ready-to-ship`),
    };
  }

  handleAddToCart() {
    if (!this.props.selectedVariantID) {
      return;
    }

    this.addToCart(this.props.selectedVariantID);

    if (this.props.isMobileOptionsOpen) {
      this.props.hideMobileOptions();
    }
  }

  handleAddToCartMobile() {
    if (!this.props.isMobileOptionsOpen) {
      this.props.showMobileOptions();

      return;
    }

    this.handleAddToCart();
  }

  handleVariantClick(id) {
    this.props.updateSelectedVariant(this.props.productKey, id);

    if (id) {
      trackSelectSize(this.props.analytics.category, _find(this.props.variants, { id }).attributes.options.size);
    }
  }

  handleQuantityUpdate(quantity) {
    this.setState({ quantity });
  }

  handleLaunchShippingModal() {
    trackShowDeliveryMessage(this.props.analytics.category);
    this.props.showModal(`shipping`);
  }

  handleResize() {
    if (mQ(`medium-up`)) {
      this.setState({
        isDesktop: true,
      });
    } else {
      this.setState({
        isDesktop: false,
      });
    }
  }

  getLowestPrice() {
    const prices = this.props.variants.map((variant) => { return variant.attributes.price; });
    const lowestPrice = Math.min(...prices);

    return currencyFormat(lowestPrice, this.props.locale, false);
  }

  getSizes() {
    return this.props.variants.length;
  }

  isUsMattress() {
    const mattressTypes = [ CASPER_ESSENTIAL, CASPER_CORE, CASPER_WAVE ];

    return mattressTypes.indexOf(this.props.productType) >= 0 && locale(this.props.locale).isUS();
  }

  renderShipsBy() {
    if (!this.props.selectedVariantID) {
      return;
    }

    const shippingNotificationClasses = cx(`shipping-notification`, `header-text`);

    return (
      <button
        className={shippingNotificationClasses}
        onClick={this.handleLaunchShippingModal}
        type="button"
      >
        <ProductAvailability {...this.getAvailabilityProps()} />
      </button>
    );
  }

  renderRatingTrigger() {
    if (locale(this.props.locale).isUS() && this.props.productType === `mattress`) {
      const storesReviewTotal = _reduce(this.props.t.ratingsStores, (accum, store) => {
        if (store.id === `casper`) {
          return accum + this.props.casperReviewsTotalCount;
        }

        return accum + store.totalCount;
      }, 0);

      const storesAvgRatingTotalCountProduct = _reduce(this.props.t.ratingsStores, (accum, store) => {
        if (store.id === `casper`) {
          return accum + (this.props.casperReviewsAvgRating * this.props.casperReviewsTotalCount);
        }

        return accum + (store.avgRating * store.totalCount);
      }, 0);

      const avgRating = _round((storesAvgRatingTotalCountProduct / storesReviewTotal), 1);

      return (
        <PurchaserRating
          avgRating={avgRating}
          hideCountMobile={true}
          parenthesis={true}
          totalCount={storesReviewTotal}
        />
      );
    }

    return (
      <PurchaserRating
        avgRating={this.props.casperReviewsAvgRating}
        hideCountMobile={true}
        parenthesis={true}
        totalCount={this.props.casperReviewsTotalCount}
      />
    );
  }

  renderReviewsLink(id) {
    if (id !== `casper`) {
      return null;
    }
    const href = optionalAttribute(`href`, constructPagePath(this.props.locale, `${this.props.productKey}-reviews`), !this.props.showBVReviews);
    const onClick = optionalAttribute(`onClick`, this.handleReviewsJumpClick, this.props.showBVReviews);

    return (
      <a
        className={styles[`rating-popover-store-link`]}
        {...href}
        {...onClick}
      >
        {this.props.t.readAllReviews}
      </a>
    );
  }

  renderRatingContent() {
    if (locale(this.props.locale).isUS() && this.props.productType === `mattress`) {
      return (
        <div>
          {this.props.t.ratingsStores.map((store) => {
            const avgRating = store.id === `casper` ? this.props.casperReviewsAvgRating : store.avgRating;
            const totalCount = store.id === `casper` ? this.props.casperReviewsTotalCount : store.totalCount;
            const storeClass = cx(`rating-popover-store`, {
              'rating-popover-store--secondary': store.id !== `casper`,
            });

            return (
              <div
                className={storeClass}
                key={store.id}
              >
                <img
                  alt={store.id}
                  className={styles[`rating-popover-store-image`]}
                  src={`/assets-spa/images/${store.imagePath}`}
                />
                <PurchaserRating
                  avgRating={avgRating}
                  displayType={store.id === `casper` ? `row` : `column`}
                  parenthesis={false}
                  plusKey={store.id !== `casper`}
                  showAverage={false}
                  totalCount={totalCount}
                />
                {this.renderReviewsLink(store.id)}
              </div>
            );
          })}
          <div
            className={styles[`rating-disclaimer`]}
            dangerouslySetInnerHTML={{__html: this.props.t.casperReviewsDisclaimer}}
          />
        </div>
      );
    }

    const sortedRatings = this.props.ratings.sort(function(a, b) {
      return b.RatingValue - a.RatingValue;
    });

    const reviewsOnClick = optionalAttribute(`reviewsOnClick`, this.handleReviewsJumpClick, this.props.showBVReviews);

    return (
      <ProductRatings
        {...reviewsOnClick}
        product={this.props.productType}
        ratings={sortedRatings}
        totalCount={this.props.casperReviewsTotalCount}
      />
    );
  }

  renderQuantityDropdown() {
    if (!this.props.showQuantitySelector) {
      return null;
    }

    const createListOfNumbers = () => {
      return _map([ 1, 2, 3, 4, 5 ], (n) => ({
        label: n.toString(),
        value: n,
      }));
    };

    return (
      <Dropdown
        containerStyles={styles[`dropdown--quantity`]}
        onClick={this.handleQuantityUpdate}
        options={createListOfNumbers()}
        selectedValue={this.state.quantity}
        showSelectedOptional={true}
      />
    );
  }

  renderDesktopPurchaser() {
    const sizeOptions = this.getSizeOptions();

    if (!this.state.isDesktop) {
      return;
    }

    if (sizeOptions.length === 1) {
      return (
        <div className={styles[`desktop-purchaser--single`]}>
          <div className={styles[`desktop-purchaser--single-action`]}>
            <p className={styles[`desktop-purchaser--single-price`]}>
              {sizeOptions[0].optional.price}
            </p>
            <div className={styles[`group-container`]}>
              {this.renderShipsBy()}
              <button
                className={styles[`add-to-cart--single`]}
                onClick={this.handleAddToCart}
                type="button"
              >
                {this.props.updatingCart ? <SpinningLoader /> : this.props.t.addToCartCta}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (this.isBuyPage()) {
      const atcClass = cx(`add-to-cart`, `button-width--buy-page`);

      return (
        <div className={styles[`desktop-purchaser`]}>
          <div className={styles[`group-container`]}>
            <div className={styles[`header-text`]}>
              {`${this.props.t.starting} ${this.getLowestPrice()}`}
            </div>
            <div>
              {`${this.getSizes()} ${this.props.t.sizes}`}
            </div>
          </div>
          <div className={styles[`group-container`]}>
            <Link
              className={atcClass}
              to={constructPagePath(this.props.locale, this.buyMap[this.props.productType])}
            >
              {this.props.t.buyPageCta}
            </Link>
          </div>
        </div>
      );
    }

    const dropdownHeaderClasses = cx(`dropdown-header`, `header-text`);
    const optionsClasses = cx({
      'dropdown-options--eu': !locale(this.props.locale).isNA(),
    });

    return (
      <div className={styles[`desktop-purchaser`]}>
        <div className={styles[`group-container`]}>
          <p className={dropdownHeaderClasses}>{this.props.t.selectSize}</p>
          <Dropdown
            containerStyles={styles[`dropdown--variant`]}
            onClick={this.handleVariantClick}
            options={sizeOptions}
            optionsStyles={optionsClasses}
            selectedValue={this.props.selectedVariantID}
            showSelectedOptional={true}
          />
        </div>
        {this.renderQuantityDropdown()}
        <div className={styles[`group-container`]}>
          {this.renderShipsBy()}
          <button
            className={styles[`add-to-cart`]}
            onClick={this.handleAddToCart}
            type="button"
          >
            {this.props.updatingCart ? <SpinningLoader /> : this.props.t.addToCartCta}
          </button>
        </div>
      </div>
    );
  }

  getMobileButtonContents() {
    if (this.props.updatingCart) {
      return <SpinningLoader />;
    } else if (!this.props.isMobileOptionsOpen) {
      return this.props.t.selectSize;
    }

    return this.props.t.addToCartCta;
  }

  renderMobilePurchaser() {
    if (this.state.isDesktop) {
      return;
    }

    const sizeOptions = this.getSizeOptions(true);
    const shipsBy = this.props.selectedVariantID ? this.getAvailabilityProps() : null;
    const addToCartClass = cx(`add-to-cart`, {
      'add-to-cart--disabled': this.props.isMobileOptionsOpen && !this.props.selectedVariantID,
    });
    const mobilePurchaserClass = cx(`mobile-purchaser`, {
      'mobile-purchaser--shown': this.props.mobileBuyBarState || this.isBuyPage(),
      'mobile-purchaser--buy-page': this.isBuyPage(),
    });

    const renderProductSelector = () => {
      if (sizeOptions.length > 1) {
        return (
          <MobileProductSelector
            handleQuantityChange={this.handleQuantityUpdate}
            hideMobileProductSelect={this.props.hideMobileOptions}
            onClick={this.handleVariantClick}
            options={sizeOptions}
            selectedValue={this.props.selectedVariantID}
            shipsBy={shipsBy}
            showQuantitySelector={this.props.showQuantitySelector}
          />
        );
      }
    };

    if (this.isBuyPage()) {
      return (
        <div className={mobilePurchaserClass}>
          <div className={styles[`mobile-atc-container`]}>
            <div className={styles[`mobile-atc-group`]}>
              <div className={styles[`mobile-atc-header`]}>
                {`${this.props.t.starting} ${this.getLowestPrice()}`}
              </div>
              <div className={styles[`mobile-atc-text`]}>
                {`${this.getSizes()} ${this.props.t.sizes}`}
              </div>
            </div>
            <Link
              className={styles[`add-to-cart`]}
              to={constructPagePath(this.props.locale, this.buyMap[this.props.productType])}
            >
              {this.props.t.buyPageCta}
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className={mobilePurchaserClass}>
        {renderProductSelector()}
        <div className={styles[`mobile-atc-container`]}>
          {this.renderShipsBy()}
          <button
            className={addToCartClass}
            onClick={this.handleAddToCartMobile}
            type="button"
          >
            {this.getMobileButtonContents()}
          </button>
        </div>
      </div>
    );
  }

  renderSubtitle() {
    const debouncedReviewsPopoverAnalytics = _debounce(trackShowReviewsPopover.bind(this, this.props.analytics.category, this.props.casperReviewsAvgRating), hoverDebounce);

    if (this.props.subtitle) {
      return this.props.subtitle;
    }

    if (!this.shouldShowRatings()) {
      return null;
    }

    return (
      <Popover
        containerStyles={styles[`rating-popover`]}
        content={this.renderRatingContent()}
        showCallback={debouncedReviewsPopoverAnalytics}
        styles={styles[`rating-popover-content`]}
        trigger={this.renderRatingTrigger()}
      />
    );
  }

  renderShippingModal() {
    const {shipsByTooltip} = this.props.t.products[this.props.productKey] || {};
    const {available, unavailable} = shipsByTooltip;

    this.shipsByContent = this.getAvailabilityProps().isReadyToShip ? available : unavailable;

    return (
      <Modal
        isCloseWhite={true}
        keyName="shipping"
      >
        <ShippingModal subheading={this.shipsByContent} />
      </Modal>
    );
  }

  handleReviewsJumpClick() {
    const reviewsID = `#reviews`;
    const reviewsEl = document.querySelector(reviewsID);

    if (!reviewsEl) return null;

    scrollToElementWithBuyBar(reviewsEl);
  }

  renderReviewsJump() {
    const {reviews} = this.props.t.products[this.props.productKey] || {};

    if (!locale(this.props.locale).isUS() || !reviews) {
      return null;
    }

    return (
      <button
        className={styles[`reviews-jump`]}
        onClick={this.handleReviewsJumpClick}
      >
        {reviews}
      </button>
    );
  }

  render() {
    const sizeOptions = this.getSizeOptions();

    const containerClass = cx(`container`, {
      'container--header-hidden': this.props.headroomState,
      'container--single': sizeOptions.length === 1,
      'container--buy-page': this.isBuyPage(),
    });

    return (
      <div>
        <div className={containerClass}>
          <div className={styles[`product-info`]}>
            <div className={styles[`product-wrapper`]}>
              <h1 className={styles[`product-name`]}>
                {this.props.t.products[this.props.productKey].title}
              </h1>
              {this.renderReviewsJump()}
            </div>
            {this.renderSubtitle()}
          </div>
          {this.renderDesktopPurchaser()}
        </div>
        {this.renderMobilePurchaser()}
        {this.renderShippingModal()}
      </div>
    );
  }
}

BuyBar.propTypes = {
  analytics: PropTypes.shape({
    cart: PropTypes.object.isRequired,
    category: PropTypes.string.isRequired,
  }).isRequired,
  casperReviewsAvgRating: PropTypes.number,
  casperReviewsTotalCount: PropTypes.number,
  fetchingVariantsSuccess: PropTypes.bool.isRequired,
  headroomState: PropTypes.bool,
  isMobileOptionsOpen: PropTypes.bool,
  locale: PropTypes.string.isRequired,
  mobileBuyBarState: PropTypes.bool,
  productKey: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
  ratings: PropTypes.array,
  selectedVariantID: PropTypes.string.isRequired,
  showBVReviews: PropTypes.bool,
  showQuantitySelector: PropTypes.bool,
  showRatings: PropTypes.bool,
  subtitle: PropTypes.node,
  t: PropTypes.shape({
    addToCartCta: PropTypes.string.isRequired,
    calloutMsg: PropTypes.string,
    casperReviewsDisclaimer: PropTypes.string,
    products: PropTypes.objectOf(PropTypes.shape({
      shipsByTooltip: PropTypes.object,
      title: PropTypes.string.isRequired,
      reviews: PropTypes.string,
    })),
    ratingsStores: PropTypes.array,
    readAllReviews: PropTypes.string,
    reviewContent: PropTypes.string,
    selectSize: PropTypes.string.isRequired,
  }).isRequired,
  variants: PropTypes.array.isRequired,
};

BuyBar.contextTypes = {
  queue: PropTypes.object,
};

const mapStateToProps = (state, props) => ({
  analytics: {
    cart: _get(state, `cart`),
    category: _get(state, `product.product.category`),
  },
  casperReviewsAvgRating: _get(state, `reviews.meta.averageProductRating`),
  casperReviewsTotalCount: _get(state, `reviews.meta.totalReviewsForProduct`),
  featureShowMattressBuyBar: _get(state, `features.showMattressBuyBar`),
  fetchingVariantsSuccess: _get(state, `product.fetchingVariantsSuccess`),
  headroomState: _get(state, `UI.headroomState`),
  isAddingToCart: _get(state, `cart.adding`),
  isMobileOptionsOpen: _get(state, `UI.mobileProductSelectShown`),
  locale: _get(state, `i18n.locale`),
  mobileBuyBarState: _get(state, `UI.mobileBuyBarState`),
  productType: _get(state, `product.productType`),
  ratings: _get(state, `reviews.meta.ratingDistribution`),
  selectedVariantID: _get(state, `selectedVariants.${props.productKey}.id`),
  showBVReviews: _get(state, `features.showBVReviews`),
  updatingCart: _get(state, `cart.updating`),
  variants: _get(state, `product.variants`),
});

const mapDispatchToProps = (dispatch) => ({
  addToCart: (selectedVariantID, selectedQuantity) => {
    const items = [{
      variant_id: parseInt(selectedVariantID, 10), // eslint-disable-line camelcase
      quantity: parseInt(selectedQuantity, 10),
    }];
    dispatch(addToCart(items));
  },

  hideMobileOptions: () => {
    dispatch(hideMobileProductSelect());
  },

  setFeature: (type) => {
    dispatch(setFeature({[type]: true}));
  },

  setHeadroomState: (state) => {
    dispatch(setHeadroomState(state));
  },

  setBuyBarState: (state) => {
    dispatch(setBuyBarState(state));
  },

  showMobileOptions: () => {
    dispatch(showMobileProductSelect());
  },

  showModal: (key) => {
    dispatch(showModal(key));
  },

  updateSelectedVariant: (productKey, id) => {
    dispatch(updateSelectedVariant(productKey, id));
  },

  getReviews: (options) => {
    dispatch(fetchReviews(options));
  },

  getProductData: (slug, locale) => {
    dispatch(fetchProduct(slug, locale));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(localize(BuyBar, translations));
